import { Bar } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import styled from 'styled-components';

import { externalTooltipHandler } from '@/_pages/interwill/components/Dashboard/helpers';
import { WidgetContainer } from '@/features/vfo/components/WidgetContainer';
import { COLORS } from '@/shared/constants/colors';
import { BFC } from '@/shared/types';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export type BarChartItem = {
  label: string;
  value: number;
  color: string;
};

type VfoBarChartProps = {
  title: string;
  items: BarChartItem[];
  subTitle?: string;
};

export const VfoBarChart: BFC<VfoBarChartProps> = ({ title, subTitle, items, ...props }) => {
  const maxValue = Math.max(...items.map((item) => item.value));

  const getBarOptions = (maxLimit: number): ChartOptions<'bar'> => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (tooltipProps) =>
          externalTooltipHandler({
            ...tooltipProps,
            valueType: 'value',
            chartType: 'bar',
            stackBars: false,
          }),
      },
      datalabels: {
        font: {
          size: 14,
          weight: 'bold',
          family: 'Manrope',
        },
        color: COLORS.systemDark300,
        anchor: 'start',
        align: 'end',
        offset: 4,
      },
    },
    scales: {
      x: {
        display: false,
        max: Math.max(maxLimit, 1),
        grid: { display: false },
      },
      y: {
        display: false,
        grid: { display: false },
      },
    },
    font: { size: 0 },
  });

  return (
    <BarChartWrapper title={title} subTitle={subTitle} {...props}>
      <Statuses>
        {items.map((item) => (
          <StatusItem key={item.label}>
            <BarChartContainer>
              <Bar
                data={{
                  labels: [item.label],
                  datasets: [
                    {
                      label: title,
                      data: [item.value],
                      backgroundColor: item.color,
                      barThickness: 27,
                      borderRadius: 4,
                      borderSkipped: false,
                    },
                  ],
                }}
                options={getBarOptions(maxValue)}
                plugins={[ChartDataLabels]}
              />
            </BarChartContainer>
            <LegendValue>{item.label}</LegendValue>
          </StatusItem>
        ))}
      </Statuses>
    </BarChartWrapper>
  );
};

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const BarChartContainer = styled.div`
  height: 27px;
  width: 100%;
`;

const BarChartWrapper = styled(WidgetContainer)`
  flex: 1;
`;

const LegendValue = styled.span`
  color: ${COLORS.systemDark300};
  line-height: 20px;
`;

const Statuses = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  height: 100%;
`;
